import React, {Fragment, useContext, useEffect, useMemo, useState} from 'react';
import { string, func } from 'prop-types';
import {
  DrawerBody,
  DrawerFooter,
  Typography,
  DrawerHeader,
  LoadingButton
} from '@one-thd/sui-atomic-components';
import { useStore } from "@thd-nucleus/experience-context";
import {
  extend,
  QueryProvider,
} from "@thd-nucleus/data-sources";
import { OPERATION_ADD_ITEMS, SCREENS } from "../../AddToConstants";
import { AddToContext } from "../../AddToContext";
import { MiniListsSummary } from "./mini-list-summary/MiniListsSummary";
import { MiniProductList } from "./mini-product-list/MiniProductList";
import { useUpdateList, updateListModel } from "../../hooks/useLists";
import { AbandonSelectionWarning } from "./AbandonSelectionWarning";

const QueryHost = ({ children }) => { return children };
QueryHost.dataModel = extend(MiniListsSummary, MiniProductList);

export const CopyList = ({ listId, onClose, onMaterialsAdded }) => {
  const [selectedList, setSelectedList] = useState(null);
  const [selectedListItems, setSelectedListItems] = useState({});
  const {
    setScreen,
    screen,
    setSuccessMessage,
    abandonWarning,
    setAbandonWarning,
    canShowWarning,
    setCanShowWarning
  } = useContext(AddToContext);
  const store = useStore({ varnish: true, online: false });
  const { membershipInformation } = store;

  const { updateList, updateListResponse } = useUpdateList({
    id: listId,
    operationName: OPERATION_ADD_ITEMS,
  });

  const { data: addToListData, loading } = updateListResponse || {};

  const totalItemsSelected = useMemo(() => {
    return Object.values(selectedListItems).reduce((acc, items) => acc + items.length, 0);
  }, [selectedListItems]);

  useEffect(() => {
    const shouldShowWarning = Boolean(totalItemsSelected);
    if (canShowWarning !== shouldShowWarning) {
      setCanShowWarning(shouldShowWarning);
    }
    return () => {
      setCanShowWarning(false);
    }
  }, [totalItemsSelected]);

  useEffect(() => {
    if (addToListData) {
      setScreen(SCREENS.DEFAULT);
      const productsAdded = Object.values(selectedListItems).flat();
      setSuccessMessage(`${productsAdded?.length} items successfully added to list`);
      setSelectedListItems({});
      setSelectedList(null);
      onMaterialsAdded();
    }
  }, [addToListData]);

  const onListClick = (currentList) => {
    if (currentList) {
      const { id, name, itemCount } = currentList;
      setSelectedList({ listId: id, name, totalItems: itemCount });
      setScreen(SCREENS.COPY_LIST_PRODUCTS);
    }
  }

  const onBackPress = (__event, overrideWarning = false) => {
    if (screen === SCREENS.COPY_LIST_PRODUCTS) {
      setScreen(SCREENS.COPY_LIST_SUMMARY);
      setSelectedList(null);
    } else {
      const showWarning = canShowWarning && !overrideWarning;
      if (showWarning) {
        setAbandonWarning({ show: true, action: 'BACK' });
      } else {
        setScreen(SCREENS.DEFAULT);
      }
    }
  }

  const onItemSelectionChange = ({ listId: currentListId, itemId, quantity }) => {
    if (loading) return false;
    const currentListItems = selectedListItems[currentListId] || [];
    const isThisItemSelected = currentListItems.some(item => item.itemId === itemId);
    if (isThisItemSelected) {
      setSelectedListItems(prevItems => ({
        ...prevItems,
        [currentListId]: currentListItems.filter(item => item.itemId !== itemId)
      }));
    }
    else {
      setSelectedListItems(prevItems => ({
        ...prevItems,
        [currentListId]: [...currentListItems, { itemId, quantity }]
      }));
    }
  }

  const onListSelectAll = (currentListId, items) => {
    if (loading) return false;
    const currentListItems = selectedListItems[currentListId] || [];
    if (currentListItems.length === items.length) {
      // If all items are already selected, deselect all
      setSelectedListItems(prevItems => ({
        ...prevItems,
        [currentListId]: []
      }));
    } else {
      setSelectedListItems(prevItems => ({
        ...prevItems,
        [currentListId]: items.map(item => ({ itemId: item.itemId, quantity: item.quantity }))
      }));
    }
  };

  const handleSubmit = () => {
    const productsToAdd = Object.values(selectedListItems).flat();
    if (productsToAdd?.length > 0) {
      updateList({
        variables: {
          products: productsToAdd
        }
      });
    }
  }

  const onDrawerClose = (__event, overrideWarning = false) => {
    const showWarning = canShowWarning && !overrideWarning;
    if (showWarning) {
      setAbandonWarning({ show: true, action: 'CLOSE' });
    } else {
      onClose();
    }
  }

  return (
    <>
      <DrawerHeader
        data-testid="add-to-drawer-header"
        title={`Copy from ${selectedList?.name || 'Your Lists'}`}
        onClose={onDrawerClose}
        onBack={onBackPress}
      />
      <DrawerBody>
        <div className="sui-pb-4">
          <Typography>
            Use your existing lists to jumpstart this one. Simply copy entire lists or click the arrow to select specific items.
          </Typography>
        </div>
        <QueryProvider
          cacheKey={'copy-list'}
          defaultVariables={{
            isBrandPricingPolicyCompliant: true,
            loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership
          }}
        >
          <QueryHost>
            {
              screen === SCREENS.COPY_LIST_SUMMARY && (
                <MiniListsSummary
                  onListClick={onListClick}
                  selectedListItems={selectedListItems}
                  onItemSelectAll={onListSelectAll}
                  currentListId={listId}
                />
              )
            }
            {
              screen === SCREENS.COPY_LIST_PRODUCTS && (
                <MiniProductList
                  listId={selectedList?.listId}
                  totalItems={selectedList?.totalItems}
                  onItemSelect={onItemSelectionChange}
                  onItemSelectAll={onListSelectAll}
                  selectedItems={selectedListItems[selectedList?.listId] || []}
                  totalItemsSelected={totalItemsSelected}
                />
              )
            }
          </QueryHost>
        </QueryProvider>

        <AbandonSelectionWarning
          open={abandonWarning?.show}
          handleClose={() => setAbandonWarning({ show: false, action: '' })}
          onConfirm={(__event) => {
            if (abandonWarning?.action === 'CLOSE') {
              onDrawerClose(__event, true);
            } else if (abandonWarning?.action === 'BACK') {
              onBackPress(__event, true);
            }
            setAbandonWarning({ show: false, action: '' })
          }}
        />
      </DrawerBody>
      <DrawerFooter>
        <LoadingButton
          data-testid="copy-list-add-button"
          variant="primary"
          fullWidth
          onClick={handleSubmit}
          type="submit"
          loading={loading}
        >
          Add {totalItemsSelected} Items
        </LoadingButton>
      </DrawerFooter>
    </>
  );
};

CopyList.displayName = 'CopyList';
CopyList.propTypes = {
  listId: string.isRequired,
  onClose: func.isRequired,
  onMaterialsAdded: func.isRequired
};
CopyList.defaultProps = {};
CopyList.dataModel = extend(updateListModel);
